import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import API from "../api";

const TWO_WHEELER = ["Hero","Honda","TVS","Bajaj","Yamaha","Suzuki","Royal Enfield","KTM","Ola Electric","Ather Energy","Revolt"];
const FOUR_WHEELER = ["Maruti Suzuki","Hyundai","Tata Motors","Mahindra","Kia","Toyota","MG Motor","Skoda","Volkswagen","Ford","Renault"];
const OTHER = ["Hotel / Resort", "Real Estate", "Retail Shop", "Restaurant", "Clinic", "Other"];

export default function Signup() {
  const [form, setForm] = useState({ email: "", password: "", businessName: "", brand: "", city: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await API.post("/auth/signup", form);
      login(res.data.user, res.data.token);
      navigate("/app/settings");
    } catch (err) {
      setError(err.response?.data?.error || "Signup failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.wrapper}>
      <div style={s.card}>

        <div style={s.logo}>
          <span style={s.logoIcon}>⬡</span>
          <div>
            <div style={s.logoText}>BotSaathi</div>
            <div style={s.logoSub}>Create Your Account</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={s.form}>
          <div style={s.title}>Get Started Free</div>
          <div style={s.subtitle}>Setup takes 2 minutes. Bot goes live in 24 hours.</div>

          {error && <div style={s.error}>{error}</div>}

          <div style={s.field}>
            <label style={s.label}>Business Name *</label>
            <input name="businessName" value={form.businessName} onChange={handleChange}
              placeholder="e.g. Mehrotra Hero Agra" required style={s.input}
              onFocus={e => e.target.style.borderColor = "var(--accent)"}
              onBlur={e => e.target.style.borderColor = "var(--border)"} />
          </div>

          <div style={s.field}>
            <label style={s.label}>Business Type *</label>
            <select name="brand" value={form.brand} onChange={handleChange} required style={s.select}>
              <option value="">-- Select --</option>
              <optgroup label="── Personal Bot">
                <option value="Personal">🤖 Personal AI Bot</option>
              </optgroup>
              <optgroup label="── 2-Wheeler">
                {TWO_WHEELER.map(b => <option key={b} value={b}>{b}</option>)}
              </optgroup>
              <optgroup label="── 4-Wheeler">
                {FOUR_WHEELER.map(b => <option key={b} value={b}>{b}</option>)}
              </optgroup>
              <optgroup label="── Other">
                {OTHER.map(b => <option key={b} value={b}>{b}</option>)}
              </optgroup>
            </select>
          </div>

          <div style={s.field}>
            <label style={s.label}>City</label>
            <input name="city" value={form.city} onChange={handleChange}
              placeholder="e.g. Mumbai, Dubai, London" style={s.input}
              onFocus={e => e.target.style.borderColor = "var(--accent)"}
              onBlur={e => e.target.style.borderColor = "var(--border)"} />
          </div>

          <div style={s.field}>
            <label style={s.label}>Email *</label>
            <input name="email" type="email" value={form.email} onChange={handleChange}
              placeholder="you@business.com" required style={s.input}
              onFocus={e => e.target.style.borderColor = "var(--accent)"}
              onBlur={e => e.target.style.borderColor = "var(--border)"} />
          </div>

          <div style={s.field}>
            <label style={s.label}>Password *</label>
            <input name="password" type="password" value={form.password} onChange={handleChange}
              placeholder="Min 6 characters" required style={s.input}
              onFocus={e => e.target.style.borderColor = "var(--accent)"}
              onBlur={e => e.target.style.borderColor = "var(--border)"} />
          </div>

          <button type="submit" disabled={loading} style={s.btn}>
            {loading ? "Creating account..." : "🚀 Create Account"}
          </button>

          <div style={{ textAlign: "center", fontSize: 13, color: "var(--text-muted)" }}>
            Already have an account?{" "}
            <Link to="/login" style={{ color: "var(--accent)", textDecoration: "none" }}>Login →</Link>
          </div>

          <div style={{ fontSize: 11, color: "var(--text-muted)", textAlign: "center", lineHeight: 1.6 }}>
            By signing up you agree to our{" "}
            <a href="/terms-of-service" style={{ color: "var(--accent)" }}>Terms</a> and{" "}
            <a href="/privacy-policy" style={{ color: "var(--accent)" }}>Privacy Policy</a>
          </div>
        </form>
      </div>
    </div>
  );
}

const s = {
  wrapper: {
    minHeight: "100vh", background: "var(--bg-primary)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
  },
  card: {
    background: "var(--bg-card)", border: "1px solid var(--border)",
    borderRadius: "var(--radius-lg)", padding: "40px",
    width: "100%", maxWidth: 440, boxShadow: "var(--shadow)",
  },
  logo: { display: "flex", alignItems: "center", gap: 12, marginBottom: 28 },
  logoIcon: { fontSize: 32, color: "var(--accent)" },
  logoText: { fontFamily: "var(--font-mono)", fontSize: 18, fontWeight: 700 },
  logoSub: { fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font-mono)", textTransform: "uppercase", letterSpacing: "0.05em" },
  form: { display: "flex", flexDirection: "column", gap: 14 },
  title: { fontSize: 20, fontWeight: 700, color: "var(--text-primary)" },
  subtitle: { fontSize: 13, color: "var(--text-secondary)", marginTop: -6 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 12, color: "var(--text-secondary)", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" },
  input: {
    background: "var(--bg-elevated)", border: "1px solid var(--border)",
    borderRadius: "var(--radius)", padding: "10px 14px",
    color: "var(--text-primary)", fontSize: 14,
    fontFamily: "var(--font-body)", outline: "none", transition: "border-color 0.15s"
  },
  select: {
    background: "var(--bg-elevated)", border: "1px solid var(--border)",
    borderRadius: "var(--radius)", padding: "10px 14px",
    color: "var(--text-primary)", fontSize: 14,
    fontFamily: "var(--font-body)", outline: "none", cursor: "pointer"
  },
  btn: {
    background: "var(--accent)", color: "#000", border: "none",
    borderRadius: "var(--radius)", padding: "12px", fontSize: 14,
    fontWeight: 700, cursor: "pointer", marginTop: 4,
  },
  error: {
    background: "rgba(255,77,77,0.1)", border: "1px solid rgba(255,77,77,0.3)",
    borderRadius: "var(--radius)", padding: "10px 14px",
    color: "var(--accent-red)", fontSize: 13,
  },
};